package fr.manyverse.app.infrastructure.error

import fr.manyverse.app.AppLogger
import fr.manyverse.app.domain.error.AudioError
import fr.manyverse.app.domain.error.AuthenticationError
import fr.manyverse.app.domain.error.CharacterError
import fr.manyverse.app.domain.error.DomainException
import fr.manyverse.app.domain.error.ImageError
import fr.manyverse.app.domain.error.NetworkError
import fr.manyverse.app.domain.error.TableError
import fr.manyverse.app.domain.error.UnexpectedError
import io.github.jan.supabase.auth.exception.AuthErrorCode
import io.github.jan.supabase.auth.exception.AuthRestException
import kotlinx.io.IOException

private const val TAG = "SupabaseErrorMapper"

/**
 * Refus d'auth provoqués par la saisie ou le rythme de l'utilisateur, pas par un défaut de l'app :
 * code OTP faux ou expiré (Supabase renvoie `otp_expired` dans les deux cas), trop de demandes,
 * adresse e-mail refusée.
 */
private val EXPECTED_AUTH_ERRORS = setOf(
    AuthErrorCode.OtpExpired,
    AuthErrorCode.OverEmailSendRateLimit,
    AuthErrorCode.OverRequestRateLimit,
    AuthErrorCode.EmailAddressInvalid
)

/**
 * Maps infrastructure exceptions to domain exceptions.
 * This function translates technical errors from Supabase, Ktor, and other infrastructure
 * components into domain-level exceptions that the business logic can understand.
 *
 * This maintains the hexagonal architecture by preventing infrastructure exceptions
 * from leaking into the domain layer.
 *
 * Seules les erreurs anormales partent dans Sentry. Une panne réseau ou un refus d'auth dû à la
 * saisie de l'utilisateur est attendu : il devient un simple breadcrumb, qui reste visible dans
 * le détail de la prochaine erreur envoyée.
 *
 * @param exception The infrastructure exception to map
 * @param context Additional context about where the error occurred (e.g., "auth", "character", "session")
 * @return A domain exception with appropriate type and message
 */
fun mapToDomainException(exception: Throwable, context: String = ""): DomainException {
    // CancellationException is a normal part of coroutine lifecycle — never treat it as an error
    if (exception is kotlinx.coroutines.CancellationException) throw exception

    logInfrastructureFailure(TAG, "Mapping exception [context=$context]", exception)

    val message = exception.message ?: "An error occurred"
    val lowerMessage = message.lowercase()

    // Réseau d'abord, quel que soit le contexte : supabase-kt enveloppe toute panne de transport
    // dans une HttpRequestException et laisse passer les timeouts de Ktor, toutes deux des IOException
    if (exception.isNetworkFailure()) {
        return NetworkError(extractUserMessage(message), exception)
    }

    // Authentication-related errors
    if (context == "auth" || isAuthenticationError(lowerMessage)) {
        return AuthenticationError(extractUserMessage(message), exception)
    }

    // Character-related errors
    if (context == "character" || isCharacterError(lowerMessage)) {
        return CharacterError(extractUserMessage(message), exception)
    }

    // Image-related errors
    if (context == "image" || isImageError(lowerMessage)) {
        return ImageError(extractUserMessage(message), exception)
    }

    // Audio-related errors
    if (context == "audio" || isAudioError(lowerMessage)) {
        return AudioError(extractUserMessage(message), exception)
    }

    // Table-related errors
    if (context == "table" || isTableError(lowerMessage)) {
        return TableError(extractUserMessage(message), exception)
    }

    // NetworkError est réservé aux pannes de transport détectées plus haut : l'UI y répond par
    // « vérifiez votre connexion », ce qui serait faux pour une erreur du serveur
    return UnexpectedError(extractUserMessage(message), exception)
}

/**
 * Logge un échec d'infrastructure : en erreur, donc envoyé à Sentry, s'il est anormal ; en simple
 * breadcrumb s'il est attendu (panne réseau, refus d'auth dû à la saisie de l'utilisateur).
 *
 * Appelée par [mapToDomainException]. À utiliser directement là où un échec est rattrapé sans
 * passer par le mapper, pour que le réseau n'y crée pas d'issue Sentry.
 */
fun logInfrastructureFailure(tag: String, message: String, exception: Throwable) {
    val logMessage = "$message: ${exception::class.simpleName} - ${exception.message}"
    val isExpected = exception.isNetworkFailure() ||
            (exception is AuthRestException && exception.errorCode in EXPECTED_AUTH_ERRORS)
    if (isExpected) {
        AppLogger.warn(tag, logMessage)
    } else {
        AppLogger.error(tag, logMessage, exception)
    }
}

/**
 * Checks if the error message indicates an authentication problem.
 */
private fun isAuthenticationError(message: String): Boolean {
    return message.contains("auth") ||
            message.contains("credential") ||
            message.contains("email") ||
            message.contains("login") ||
            message.contains("sign in") ||
            message.contains("sign up") ||
            message.contains("unauthorized") ||
            message.contains("invalid credentials") ||
            message.contains("user not found") ||
            message.contains("session expired") ||
            message.contains("token")
}

/**
 * Checks if the error message indicates a character-related problem.
 */
private fun isCharacterError(message: String): Boolean {
    return message.contains("character") ||
            message.contains("zero_characters")
}

/**
 * Checks if the error message indicates an image-related problem.
 */
private fun isImageError(message: String): Boolean {
    return message.contains("storage") ||
            message.contains("upload") ||
            message.contains("image") ||
            message.contains("avatar") ||
            message.contains("file") ||
            message.contains("bucket")
}

/**
 * Checks if the error message indicates an audio-related problem.
 */
private fun isAudioError(message: String): Boolean {
    return message.contains("audio") ||
            message.contains("sound") ||
            message.contains("music") ||
            message.contains("mp3") ||
            message.contains("wav") ||
            message.contains("ogg")
}

/**
 * Checks if the error message indicates a table-related problem.
 */
private fun isTableError(message: String): Boolean {
    return message.contains("table") ||
            message.contains("game") ||
            message.contains("join") ||
            message.contains("not a member")
}

/**
 * Panne de transport : l'exception ou l'une de ses causes est une [IOException]. Couvre
 * `HttpRequestException` de supabase-kt, `HttpRequestTimeoutException` de Ktor et, sur JVM,
 * les `UnknownHostException`/`SocketException`.
 */
private fun Throwable.isNetworkFailure(): Boolean =
    generateSequence(this) { it.cause }.any { it is IOException }

/**
 * Extracts a clean, user-friendly message from the exception message.
 * Removes technical details like URLs, headers, and stack traces.
 */
private fun extractUserMessage(message: String): String {
    // Take only the first line before technical details
    val firstLine = message.lines().firstOrNull { line ->
        val trimmed = line.trim()
        // Ignore empty lines and lines containing technical details
        trimmed.isNotEmpty() &&
                !trimmed.startsWith("URL:") &&
                !trimmed.startsWith("Method:") &&
                !trimmed.startsWith("Headers:") &&
                !trimmed.startsWith("Body:") &&
                !trimmed.startsWith("Status Code:") &&
                !trimmed.startsWith("at ") &&
                !trimmed.contains("http://") &&
                !trimmed.contains("https://")
    } ?: "An error occurred"

    return firstLine.trim()
}
